/**
 * A value, once it has stopped moving.
 *
 * The gallery's search box is the caller this was written for. Every keystroke
 * in it was a new query, and a new query is a new request: typing "saison" sent
 * six, five of which were answers nobody was still waiting for, and the grid
 * blinked once per letter on the way. Debouncing the *value* rather than the
 * fetch keeps that decision where the box is — the hook underneath answers
 * whatever query it is handed, and this is the part that decides when a
 * keystroke has become one.
 *
 * The first render returns the value it was given, with no wait: an empty box
 * on arrival is already a settled question, and making the page hold its breath
 * for a quarter second before asking it would be a pause bought for nothing.
 * After that, a change is held until `delayMs` has passed with no further
 * change, so a burst collapses to its last value. A change to `delayMs` itself
 * restarts the wait, which is a thing no caller does mid-type.
 */

import { useEffect, useState } from 'react';

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
